import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  PamlabApiServiceService,
  DataService,
  RouteService,
} from '@/shared/services';
import { MatDialog } from '@angular/material/dialog';
import { courseCatalog } from '../../../../../data';
import { environment } from '../../../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { Course } from '@/core/interfaces';
import { courseExistsValidator } from '@/shared/directives/custom-validators';

interface Prerequisite {
  course: string;
}

@Component({
  selector: 'prerequisites-form',
  templateUrl: './prerequisites-form.component.html',
  styleUrls: ['./prerequisites-form.component.css'],
  standalone: false,
})
export class PrerequisitesFormComponent implements OnInit, OnDestroy {
  public height!: string;

  // observable string that observes for input value in the form field
  filteredCourses!: string[];

  // subscription for data
  private subscription: Subscription = new Subscription();

  // prerequisites form group
  public prerequisitesForm = new FormGroup({
    course: new FormControl('ACG3101-Intermediate Financial Accounting I', [
      Validators.required,
    ]),
  });

  // course catalog variable with code and name
  courseCatalogWithName: string[] = [];

  // variable to check if user form submission is same as previous submission
  prevParam: Prerequisite = {
    course: '',
  };

  constructor(
    private dialog: MatDialog,
    private apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _routeService: RouteService,
  ) {}

  ngOnInit(): void {
    this.courseCatalogWithName = courseCatalog.map(
      (course: Course) => `${course.course_code}-${course.course_name}`,
    );

    this.prerequisitesForm
      .get('course')!
      .setValidators([courseExistsValidator(this.courseCatalogWithName)]);
    this.prerequisitesSubmit();

    // observable to observes for changes in the input filed for course name
    // For Prerequisites
    this.subscription.add(
      this.prerequisitesForm
        .get('course')!
        .valueChanges.pipe(
          startWith(''),
          map((value) => {
            this.filteredCourses = this.courseCatalogWithName.filter((course) =>
              course.toLowerCase().includes(value!.toLowerCase()),
            );

            if (this.filteredCourses.length < 4) {
              this.height = this.filteredCourses.length * 50 + 'px';
            } else {
              this.height = '200px';
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  // courseInfoForm(prerequisite) get data from api by passing user input from form
  prerequisitesSubmit() {
    this._dataService.setPendingData(true);
    const course = this.getCourseCode(this.prerequisitesForm.value.course!);

    this.subscription.add(
      this.apiService
        .getTargetCourse({
          target: course.toUpperCase(),
        })
        .subscribe({
          next: (data) => {
            this._dataService.setData({ data });
            this._dataService.setPendingData(false);
          },
          error: (errorRes: HttpErrorResponse) => {
            if (errorRes.status === 401) {
              this._routeService.navigate(environment['login_url']);
            }
            this._dataService.setData({ error: errorRes.error });
            this._dataService.setPendingData(false);
          },
        }),
    );

    this.setPrevParams();
  }

  // setting form value that is submitted by user
  setPrevParams() {
    this.prevParam.course === this.prerequisitesForm.value.course;
  }

  // to check if user entered same form value from previous submission
  sameParams() {
    return (
      this.prevParam &&
      this.prevParam.course === this.prerequisitesForm.value.course
    );
  }

  // split string into 2 parts [MAC2311, Calculus 1]
  getCourseCode(str: string) {
    return str.split('-')[0];
  }

  // close popup dialog after form is submitted
  closeDialog() {
    this.dialog.closeAll();
  }
}
